import React, {useCallback, useEffect, useState} from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import TextField from "@material-ui/core/TextField";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import {makeStyles} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import debounce from "@material-ui/core/utils/debounce";
import SesionesList from "./SesionesList";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from '@material-ui/core/InputAdornment';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles(theme => ({
    buttons: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        paddingBottom: theme.spacing(2),
    },
}));

export default function UnarchiveSessionDialog({component, onSave, postMessage}) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [sessionsOptions, setSessionsOptions] = useState(null);
    const [filteredSesiones, setFilteredSesiones] = useState(null);
    const [name, setName] = useState('');
    const { t } = useTranslation();
    const [selectedItem, setSelectedItem] = useState(null);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSessionsOptions(null);
    };

    useEffect(() => {
        setFilteredSesiones(sessionsOptions)
    }, [sessionsOptions]);

    useEffect(() => {
        if (!open)
            return;

        postMessage({
            type: 'GET_ARCHIVED_SESSIONS_OPTIONS',
            data: undefined,
            responseType: 'archived-sessions-options',
            responseCallback: setSessionsOptions,
        });
    }, [open, postMessage]);

    const debouncedFilterSesiones = useCallback(
        debounce(
            (value) => {
                if (sessionsOptions === null)
                    return;

                setFilteredSesiones(
                    value !== '' ?
                        sessionsOptions.filter(paciente => paciente.nombre.toLowerCase().includes(value.toLowerCase()))
                        :
                        sessionsOptions
                );
            },
            500
        ),
        [sessionsOptions]);

    const changeName = useCallback(function(value) {
        setName(value);
        debouncedFilterSesiones(value);
    }, [debouncedFilterSesiones]);

    const onSelectItem = useCallback(index => {
        setSelectedItem(filteredSesiones[index]);
        changeName(filteredSesiones[index].nombre);
    }, [filteredSesiones, changeName]);

    return (
        <>
            {React.cloneElement(component, {
                onClick: handleClickOpen,
            })}
            <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title" fullScreen>
                <DialogTitle id="form-dialog-title">{t('UnarchiveSessionDialog.title')}</DialogTitle>
                <DialogContent style={{display: 'flex', flexDirection: 'column'}}>
                    <DialogContentText>
                        {t('UnarchiveSessionDialog.text')}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('UnarchiveSessionDialog.input-paciente')}
                        fullWidth
                        value={name}
                        onChange={ev => {
                            changeName(ev.target.value);
                            setSelectedItem(null);
                        }}
                    />
                    {selectedItem === null ?
                        <>
                            <DialogContentText style={{marginTop: 24}}>
                                {t('UnarchiveSessionDialog.choose-existing-patient')}:
                            </DialogContentText>
                            <div
                                style={{flex: 1}}
                            >
                                {filteredSesiones !== null &&
                                <SesionesList items={filteredSesiones} onSelect={onSelectItem} />}
                            </div>
                        </>
                        :
                        <div style={{display: 'flex', alignItems: 'center', marginTop: 24}}>
                            <Typography>{t('UnarchiveSessionDialog.recover')}</Typography>
                            <Button
                                color='primary'
                                onClick={() => {
                                    changeName('');
                                    setSelectedItem(null);
                                }}
                            >
                                {t('UnarchiveSessionDialog.cancel')}
                            </Button>
                        </div>
                    }
                </DialogContent>
                <DialogActions className={classes.buttons}>
                    <Button onClick={handleClose} color="primary">
                        {t('UnarchiveSessionDialog.cancel')}
                    </Button>
                    <Button
                        onClick={() => {
                            onSave(selectedItem);
                            handleClose();
                        }}
                        disabled={selectedItem === null}
                        color="primary"
                        variant="contained"
                    >
                        {t('UnarchiveSessionDialog.ok')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}